const u = (id) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1400&q=75`;

export const SITES = [
  {
    id: "giza-pyramid",
    name: "Great Pyramid of Giza",
    location: "Giza Plateau, Egypt",
    lat: 29.9792,
    lng: 31.1342,
    age: "c. 2560 BCE — or far older?",
    category: "Megalithic Precision",
    image: u("photo-1600520611035-84157ad4084d"),
    mainstream:
      "Built during the Fourth Dynasty as a royal tomb for Pharaoh Khufu — copper chisels, ramps, sledges and around two decades of organised labour.",
    hypotheses: [
      {
        title: "The Orion Correlation",
        body: "Robert Bauval and Graham Hancock noticed the three pyramids mirror Orion's Belt — as it hung in 10,500 BCE. A blueprint older than Egypt itself?",
      },
      {
        title: "The Power Plant",
        body: "Christopher Dunn reads the granite chambers, shafts and empty sarcophagus as components of a piezoelectric machine that turned Earth's vibration into energy.",
      },
      {
        title: "The Lost Inheritance",
        body: "Aligned to true north within 1/15th of a degree, yet bearing no tomb inscription — some argue the dynastic Egyptians inherited the monument rather than raised it.",
      },
    ],
    anomalies: [
      "Aligned to true north within 1/15th of a degree",
      "Eight concave faces, visible only at equinox light",
      "No mummy or funerary text ever found inside",
    ],
  },
  {
    id: "great-sphinx",
    name: "The Great Sphinx",
    location: "Giza Plateau, Egypt",
    lat: 29.9753,
    lng: 31.1376,
    age: "c. 2500 BCE — or 7000+?",
    category: "Undated Enigmas",
    image: u("photo-1613728964229-03bd6189bdad"),
    mainstream:
      "Carved from the limestone bedrock around 2500 BCE under Pharaoh Khafre as a guardian of the necropolis, its face bearing the king's likeness.",
    hypotheses: [
      {
        title: "The Water Erosion Argument",
        body: "Robert Schoch and John Anthony West point to deep vertical weathering on the enclosure walls — the signature of heavy rains Egypt last saw before c. 7000 BCE.",
      },
      {
        title: "The Original Lion",
        body: "The mismatched head may have been recarved from a much larger lion monument — one that faced the constellation Leo at the equinox dawn of 10,500 BCE.",
      },
      {
        title: "The Hall of Records",
        body: "Seismic surveys report voids beneath the paws. Edgar Cayce's readings claimed an archive from a lost civilisation sleeps below, waiting.",
      },
    ],
    anomalies: [
      "Head appears disproportionately small for the body",
      "Seismic anomalies detected beneath the paws",
      "Repair stones hint the head was reshaped long after the body",
    ],
  },
  {
    id: "stonehenge",
    name: "Stonehenge",
    location: "Wiltshire, England",
    lat: 51.1789,
    lng: -1.8262,
    age: "c. 3000–2000 BCE",
    category: "Archaeoastronomy",
    image: u("photo-1520499030345-d646f4350b30"),
    mainstream:
      "A Neolithic solar calendar and burial ground, raised in phases over centuries by Britain's early farming communities.",
    hypotheses: [
      {
        title: "The Ringing Rocks",
        body: "Strike the bluestones and they ring. Some researchers argue the circle was chosen — and built — as a resonant instrument: a temple you play.",
      },
      {
        title: "The Healing Pilgrimage",
        body: "The bluestones were hauled 180 miles from Wales. Why? Perhaps because they were believed to cure the sick — a Neolithic Lourdes.",
      },
      {
        title: "The Earth Grid",
        body: "Great-circle arcs link Stonehenge to Carnac and Giza with uncomfortable precision. A planet-wide survey system, say the geometers.",
      },
    ],
    anomalies: [
      "Bluestones quarried 180 miles away in the Preseli Hills",
      "Mortise-and-tenon woodwork joints carved into solid stone",
      "Some stones produce bell-like tones when struck",
    ],
  },
  {
    id: "gobekli-tepe",
    name: "Göbekli Tepe",
    location: "Southeastern Anatolia, Türkiye",
    lat: 37.2232,
    lng: 38.9221,
    age: "c. 9600 BCE",
    category: "Undated Enigmas",
    image:
      "https://static.prod-images.emergentagent.com/jobs/c95ca2e2-3b06-45d4-ae34-361e44d875fe/images/76118938a2b7433a1bf67133c93a7f9bdc4fe118e9a06e964cf5b58a391db525.jpeg",
    mainstream:
      "The world's oldest known monumental sanctuary — raised by hunter-gatherers some 11,600 years ago, before agriculture, pottery or the wheel.",
    hypotheses: [
      {
        title: "The Comet's Memorial",
        body: "Martin Sweatman reads the Vulture Stone as a star chart of the Taurid meteor stream impact around 10,800 BCE — a monument to a cataclysm.",
      },
      {
        title: "Buried on Purpose",
        body: "The enclosures were deliberately backfilled around 8000 BCE. Why would anyone entomb their own temple? Nobody has a comfortable answer.",
      },
      {
        title: "First Pray, Then Farm",
        body: "The site predates farming by millennia — hinting that religion, not agriculture, gathered humanity into its first towns.",
      },
    ],
    anomalies: [
      "Deliberately buried c. 8000 BCE, for reasons unknown",
      "T-pillars up to 6 metres tall and 10 tonnes",
      "Carved reliefs some read as an early zodiac order",
    ],
  },
  {
    id: "gunung-padang",
    name: "Gunung Padang",
    location: "West Java, Indonesia",
    lat: -6.9936,
    lng: 107.0646,
    age: "Terraces c. 2000 BCE — core contested to 20,000+ BCE",
    category: "Undated Enigmas",
    image: u("photo-1707371773021-d39017b761f0"),
    mainstream:
      "A natural andesite hill crowned by a megalithic terraced sanctuary, its visible stonework attributed to around 2000 BCE.",
    hypotheses: [
      {
        title: "The Oldest Pyramid?",
        body: "Danny Hilman Natawidjaja's 2023 radiocarbon paper argued deep layers hide man-made chambers 16,000–27,000 years old — igniting a global retraction battle.",
      },
      {
        title: "Sundaland's Capital",
        body: "If the deep layers are human work, the hill would anchor a lost Ice-Age civilisation drowned when the Sunda shelf flooded.",
      },
      {
        title: "The Humming Terraces",
        body: "The andesite columns ring when struck. Local lore says the hill 'sings' to warn the valleys of eruptions.",
      },
    ],
    anomalies: [
      "Electrical resistivity hints at hollow chambers at depth",
      "Core samples dated far below expected strata",
      "The 2023 paper was retracted — and the argument continues",
    ],
  },
  {
    id: "easter-island",
    name: "Easter Island (Rapa Nui)",
    location: "Polynesia, Pacific Ocean",
    lat: -27.1127,
    lng: -109.3497,
    age: "c. 1100–1600 CE",
    category: "Megalithic Precision",
    image: u("photo-1597240890437-6d9c2d4c16aa"),
    mainstream:
      "Moai ancestor figures carved by Polynesian settlers between 1100 and 1600 CE, moved on sledges and ropes from the island's quarry.",
    hypotheses: [
      {
        title: "The Walking Statues",
        body: "Oral tradition insists the moai walked. A 2012 experiment showed teams can rock tall statues forward with ropes — the legend physics-checked back to life.",
      },
      {
        title: "The Ecological Revision",
        body: "The famous 'ecocide' collapse story is contested: newer sediment studies suggest the Rapa Nui thrived sustainably until outside contact.",
      },
      {
        title: "Rongorongo",
        body: "The undeciphered tablet script resembles nothing else in Polynesia — a few see echoes of distant, older writing systems.",
      },
    ],
    anomalies: [
      "Most moai have full bodies buried up to 6 metres deep",
      "A written script left undeciphered to this day",
      "Basalt tools of surprising precision at the quarry",
    ],
  },
  {
    id: "nazca-lines",
    name: "Nazca Lines",
    location: "Nazca Desert, Peru",
    lat: -14.739,
    lng: -75.13,
    age: "c. 500 BCE – 500 CE",
    category: "Archaeoastronomy",
    image: u("photo-1601214571533-8cbac554ff5d"),
    mainstream:
      "Geoglyphs etched by removing dark desert varnish — ritual pathways and water-cult imagery made by the Nazca culture.",
    hypotheses: [
      {
        title: "Sky Beacons",
        body: "Erich von Däniken's famous provocation: guidance markings for 'sky gods'. Archaeology rejects it — the imagery never went away.",
      },
      {
        title: "The Aquifer Map",
        body: "Many glyphs trace hidden water: the lines mark where the desert's underground rivers and aquifers breathe closest to the surface.",
      },
      {
        title: "The Walked Labyrinth",
        body: "Newer fieldwork argues the figures were processional paths — designed to be walked in ritual, never meant for the eye at all.",
      },
    ],
    anomalies: [
      "Miles-long lines running arrow-straight over broken hills",
      "Glyphs fully legible only from the air",
      "Spirals and rays aligned to solstice horizons",
    ],
  },
  {
    id: "machu-picchu",
    name: "Machu Picchu",
    location: "Cusco Region, Peru",
    lat: -13.1631,
    lng: -72.545,
    age: "c. 1450 CE — foundations debated",
    category: "Megalithic Precision",
    image: u("photo-1587595431973-160d0d94add1"),
    mainstream:
      "A royal estate built for the Inca emperor Pachacuti in the 15th century, abandoned during the conquest and never found by the Spanish.",
    hypotheses: [
      {
        title: "The Older Foundation",
        body: "The finest polygonal masonry is unlike standard Inca work — some argue an earlier culture built the core, and the Inca inherited the mountaintop.",
      },
      {
        title: "The Solar Machine",
        body: "The Intihuatana stone and temple windows frame solstice sunrises with surveyor-grade precision — a calendrical instrument in the clouds.",
      },
      {
        title: "The 2021 Recalibration",
        body: "New radiocarbon dating pushed the site's occupation back to ~1420 CE, decades earlier than the textbook date — even 'settled' dates move.",
      },
    ],
    anomalies: [
      "Mortarless ashlar joints surviving five centuries of earthquakes",
      "The Intihuatana stone shows localized magnetic anomalies",
      "Two distinct masonry styles stacked on one ridge",
    ],
  },
  {
    id: "puma-punku",
    name: "Puma Punku & Tiwanaku",
    location: "La Paz Department, Bolivia",
    lat: -16.5547,
    lng: -68.6738,
    age: "c. 500–1000 CE — or 15,000 BCE?",
    category: "Megalithic Precision",
    image: u("photo-1749937465176-ed05a0f3ae6a"),
    mainstream:
      "A ceremonial complex of the Tiwanaku empire, built between 500 and 1000 CE with stone, sand and metal tools.",
    hypotheses: [
      {
        title: "The Machine-Cut Blocks",
        body: "H-blocks with perfect right angles, blind holes and repeatable modules — writers like David Hatcher Childress argue for lost machining technology.",
      },
      {
        title: "Posnansky's 15,000 Years",
        body: "Astronomer Arthur Posnansky dated the Kalasasaya alignments to c. 15,000 BCE. The claim was dismissed — and never quite dies.",
      },
      {
        title: "The Flood Ruin",
        body: "The site lies in scattered wreckage, as if a wall of water tore it apart — for some, evidence of a cataclysm at the end of the Ice Age.",
      },
    ],
    anomalies: [
      "130-ton blocks with precision internal grooves",
      "I-shaped metal clamps held the masonry together",
      "Built at 3,800 metres, far above easy agriculture",
    ],
  },
  {
    id: "baalbek",
    name: "Baalbek",
    location: "Beqaa Valley, Lebanon",
    lat: 34.007,
    lng: 36.2045,
    age: "Roman era atop debated foundations",
    category: "Megalithic Precision",
    image: u("photo-1547126627-af8df614498c"),
    mainstream:
      "The Roman sanctuary of Jupiter Heliopolitanus, raised atop earlier Phoenician sanctuaries in the first centuries CE.",
    hypotheses: [
      {
        title: "The Trilithon Problem",
        body: "Three stones of 800–1,200 tons sit high in the podium. Rome could quarry them — but how were they moved, raised and set with millimetric fit?",
      },
      {
        title: "The Older Platform",
        body: "The great foundation platform may predate Rome by centuries or more. Its true age is still argued in the engineering journals.",
      },
      {
        title: "The Giant's Lore",
        body: "Local tradition credits the walls to giants, or to Solomon's jinns. The Stone of the Pregnant Woman still 'gives birth' to folklore.",
      },
    ],
    anomalies: [
      "Heaviest architectural stones ever quarried on Earth",
      "Roman columns stand on far larger base blocks",
      "An abandoned 1,000-ton stone still attached to the quarry bedrock",
    ],
  },
  {
    id: "mohenjo-daro",
    name: "Mohenjo-daro",
    location: "Sindh, Pakistan",
    lat: 27.3244,
    lng: 68.1368,
    age: "c. 2500 BCE",
    category: "Undated Enigmas",
    image: u("photo-1773601103540-2e4da9e61970"),
    mainstream:
      "A planned metropolis of the Indus Valley Civilisation — grid streets, covered drains and standardised bricks, centuries ahead of its peers.",
    hypotheses: [
      {
        title: "The Vitrified Bricks",
        body: "Patches of fused, glassy clay fuel claims of a thermal event far beyond ancient kilns. Geology says no — the lumps keep the story warm.",
      },
      {
        title: "Mahabharata Echoes",
        body: "Passages describing sky-borne weapons are read by some as cultural memory of an ancient blast. Archaeology reads poetry; the debate persists.",
      },
      {
        title: "The Silent Exodus",
        body: "No palace, no temple, no mass graves: the city simply emptied. Where the Indus people went is one of archaeology's quiet embarrassments.",
      },
    ],
    anomalies: [
      "Skeletons found lying in the streets, unburied",
      "Grid planning and plumbing centuries ahead of peers",
      "A script still undeciphered after a century of effort",
    ],
  },
  {
    id: "yonaguni",
    name: "Yonaguni Monument",
    location: "Ryukyu Islands, Japan",
    lat: 24.4372,
    lng: 123.0125,
    age: "Submerged c. 10,000–8,000 BCE",
    category: "Sunken Civilizations",
    image:
      "https://static.prod-images.emergentagent.com/jobs/c95ca2e2-3b06-45d4-ae34-361e44d875fe/images/e904363693f7f8eb3eb0ccef3bc44b72d7c2aac4b0840f2039e25ab963e08c03.jpeg",
    mainstream:
      "A natural sandstone formation shaped by wave erosion and tectonic faulting — striking, but geology working on schedule.",
    hypotheses: [
      {
        title: "Japan's Atlantis",
        body: "Marine geologist Masaaki Kimura mapped steps, roads and a carved 'face'. He argues for a man-made monument lost to the Holocene seas.",
      },
      {
        title: "The 10,000-Year Clock",
        body: "If carved by hand, the terrace last stood above water around the end of the Ice Age — older than Göbekli Tepe, and underwater ever since.",
      },
      {
        title: "Nature's Chisel",
        body: "Critics counter that this sandstone splits into flat faces all by itself. The debate is a masterclass in how rocks lie — or don't.",
      },
    ],
    anomalies: [
      "Right-angled terraces with sharp, regular edges",
      "A carved 'wedge' stone and channels cut along the rock",
      "Features that continue below the modern sand line",
    ],
  },
  {
    id: "teotihuacan",
    name: "Teotihuacán",
    location: "State of Mexico, Mexico",
    lat: 19.6923,
    lng: -98.8437,
    age: "c. 100 BCE – 500 CE",
    category: "Archaeoastronomy",
    image: u("photo-1630646188133-c1db51adad97"),
    mainstream:
      "A multiethnic metropolis of 100,000 ruled by an unknown elite — so old that even the Aztecs found it in ruins and named it 'birthplace of the gods'.",
    hypotheses: [
      {
        title: "The Solar-System Model",
        body: "Hugh Harleston Jr. measured the Avenue of the Dead as a scale map of planetary orbits — including the outer planets nobody had seen.",
      },
      {
        title: "The Mica Enigma",
        body: "Sheets of mica, imported 2,000 miles from Brazil, line structures with no visible purpose — unless you need electrical insulation.",
      },
      {
        title: "The Mercury River",
        body: "Liquid mercury and hundreds of offerings fill the tunnel beneath the Feathered Serpent temple — an underworld model still being decoded.",
      },
    ],
    anomalies: [
      "No known writing, royal tombs or self-portraits",
      "Mica sheets imported from 2,000 miles away",
      "The whole city axis tilts ~15° off true north",
    ],
  },
  {
    id: "nan-madol",
    name: "Nan Madol",
    location: "Pohnpei, Micronesia",
    lat: 6.8453,
    lng: 158.3353,
    age: "c. 1100–1600 CE — older roots debated",
    category: "Sunken Civilizations",
    image:
      "https://static.prod-images.emergentagent.com/jobs/c95ca2e2-3b06-45d4-ae34-361e44d875fe/images/1463279a890526dca64ec80c3e2c2412243c25e2b9ae7f8ead8266872b17a355.jpeg",
    mainstream:
      "The ceremonial capital of the Saudeleur dynasty — artificial islets ringed with walls of stacked prismatic basalt, built on a coral reef.",
    hypotheses: [
      {
        title: "The Floating Logs",
        body: "Legend says the stones flew: magician brothers Olisihpa and Ohnapa levitated basalt 'logs' across the lagoon until the load sank their raft-bridge.",
      },
      {
        title: "Venice of the Pacific",
        body: "A hundred islets, canals and seawalls of columnar basalt — a logistics feat on a tiny island that modern engineers still can't fully account for.",
      },
      {
        title: "The Sunken Wings",
        body: "Divers report structures beyond the seawalls. Claims of a drowned outer city remain unverified — and utterly irresistible.",
      },
    ],
    anomalies: [
      "Estimates of moved stone approach 750,000 tons",
      "Basalt columns ring with unusual pitch when struck",
      "Royal tombs traditionally sealed off from excavation",
    ],
  },
  {
    id: "derinkuyu",
    name: "Derinkuyu",
    location: "Cappadocia, Türkiye",
    lat: 38.3736,
    lng: 34.7348,
    age: "c. 1200 BCE onward",
    category: "Subterranean Systems",
    image: u("photo-1691156553528-b04267020a48"),
    mainstream:
      "A multi-level subterranean refuge carved from soft volcanic tuff, expanded over centuries as a shelter against raids and persecution.",
    hypotheses: [
      {
        title: "The Fallout Shelter",
        body: "Eighteen levels, eighty-five metres down, room for 20,000 people plus livestock — engineered, some argue, for a catastrophe from the sky.",
      },
      {
        title: "Who Dug First?",
        body: "Hittites, Phrygians or Persians? The deepest layers have no confirmed author — the origins of the dig are genuinely open.",
      },
      {
        title: "The Breathing Shafts",
        body: "Ventilation shafts feed fresh air to the lowest level with no machinery. Some see deliberate placement over underground water veins.",
      },
    ],
    anomalies: [
      "Rolling half-ton stone doors seal corridors from inside",
      "Ventilation reaches level 18 without any pumps",
      "A claimed 9-kilometre tunnel links it to Kaymakli",
    ],
  },
];
